import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import { AiOutlineDownload } from "react-icons/ai";
import { HiArrowUpRight } from "react-icons/hi2";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";

import useReveal from "../../hooks/useReveal";
import useSEO from "../../seo/useSEO";
import { profilePageSchema, breadcrumb } from "../../seo/schemas";
import api, { mediaUrl } from "../../lib/api";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

export default function ResumeNew() {
  const [width, setWidth] = useState(1200);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [error, setError] = useState(null);

  useReveal();
  useSEO({
    title: "Resume / CV — Oguzhan Ozcelik",
    description: "Resume of Oguzhan Ozcelik, full-stack developer & Shopify expert. Download the CV (PDF) or read it online.",
    path: "/resume",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        profilePageSchema(),
        breadcrumb([{ name: "Home", path: "/" }, { name: "Resume", path: "/resume" }]),
      ],
    },
    jsonLdId: "resume-jsonld",
  });

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    let live = true;
    api.get("/api/resume")
      .then(({ data }) => {
        if (!live) return;
        const url = data?.item?.url ? mediaUrl(data.item.url) : null;
        setPdfUrl(url);
      })
      .catch((e) => { if (live) setError(e?.response?.data?.error || e.message); });
    return () => { live = false; };
  }, []);

  const scale = width > 786 ? 1.5 : 0.55;

  return (
    <main className="resume-page">
      <div className="container-x">
        <div className="reveal" style={{ marginBottom: 40, textAlign: "center" }}>
          <span className="eyebrow" style={{ justifyContent: "center" }}>Resume · CV</span>
          <h1 className="about-hero__title" style={{ marginTop: 12 }}>
            The official
            <br />
            <span className="it text-accent">paper trail.</span>
          </h1>
        </div>

        <div className="resume-actions reveal">
          {pdfUrl && (
            <a className="btn" href={pdfUrl} target="_blank" rel="noreferrer noopener" download>
              <AiOutlineDownload /> Download CV
            </a>
          )}
          <Link className="btn btn--ghost" to="/contact">
            Get in touch
            <span className="arrow"><HiArrowUpRight /></span>
          </Link>
        </div>

        {error && <p style={{ color: "var(--danger)", textAlign: "center" }}>{error}</p>}
        {!pdfUrl && !error && <p className="text-muted" style={{ textAlign: "center" }}>Loading resume…</p>}

        {pdfUrl && (
          <div className="resume-doc reveal">
            <Document
              file={pdfUrl}
              onLoadSuccess={(d) => setNumPages(d.numPages)}
              onLoadError={(e) => setError(e?.message || "Could not load PDF")}
            >
              {numPages && Array.from({ length: numPages }, (_, i) => (
                <Page
                  key={i + 1}
                  pageNumber={i + 1}
                  scale={scale}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                />
              ))}
            </Document>
          </div>
        )}

        {pdfUrl && (
          <div className="resume-actions reveal" style={{ marginTop: 32 }}>
            <a className="btn" href={pdfUrl} target="_blank" rel="noreferrer noopener" download>
              <AiOutlineDownload /> Download CV
            </a>
          </div>
        )}
      </div>
    </main>
  );
}
